import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from './ui/PageHeader';
import { PageLoader } from './ui/PageLoader';
import { AdminQueueState } from './ui/AdminQueueState';
import apiService from '../services/ApiService';
import type { AdminEventSummary } from '../types/event';

const AdminEvents: React.FC = () => {
  const [events, setEvents] = useState<AdminEventSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getAdminEvents();
      setEvents(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Admin"
        title="Events"
        description="Support view: events with registration and judging completion counts. No PII exposed."
      />
      {loading ? <PageLoader message="Loading events" /> : null}
      {!loading && error ? (
        <AdminQueueState variant="error" title="Could not load events" description={error} onRetry={() => void load()} />
      ) : null}
      {!loading && !error && events.length === 0 ? (
        <AdminQueueState
          variant="empty"
          title="No events in the system yet"
          description="Organizer-created events will appear here for support review."
        />
      ) : null}
      {!loading && !error && events.length > 0 ? (
        <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
          <table className="min-w-full text-sm">
            <thead className="bg-[#141414]/80 text-gray-400 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Event</th>
                <th className="px-4 py-3 font-medium">Organizer</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Regs</th>
                <th className="px-4 py-3 font-medium">Judges</th>
                <th className="px-4 py-3 font-medium">Scores</th>
                <th className="px-4 py-3 font-medium">Links</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.06]">
              {events.map((evt) => (
                <tr key={evt.id} className="hover:bg-white/[0.04]/40">
                  <td className="px-4 py-3">
                    <p className="font-medium text-white">{evt.title}</p>
                    <p className="text-xs text-gray-500 font-mono">/events/{evt.slug}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-300">@{evt.organizer_username}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#161616] text-gray-300">{evt.status}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-300">{evt.registration_count ?? 0}</td>
                  <td className="px-4 py-3 text-gray-300">{evt.judge_count ?? 0}</td>
                  <td className="px-4 py-3 text-gray-300">
                    <span className="text-green-400/90">{evt.finalized_score_count ?? 0}</span>
                    <span className="text-gray-600"> / </span>
                    <span className="text-amber-400/80">{evt.draft_score_count ?? 0} draft</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1 text-xs">
                      <Link
                        to={`/organizer/events/${evt.id}`}
                        className="text-emerald-400 hover:text-emerald-300 font-medium"
                      >
                        Manage
                      </Link>
                      <Link to={`/events/${evt.slug}`} className="text-orange-400 hover:text-orange-300">
                        Public
                      </Link>
                      <Link to={`/events/${evt.slug}/archive`} className="text-amber-400/90 hover:text-amber-300">
                        Archive
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

export default AdminEvents;
